// src/components/KeywordResearchTool.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './KeywordResearch.css';
import { event } from '../utils/gtm';
import UpgradeError from './UpgradeError';
import InfoTooltip from './InfoTooltip';

// Move the SerpDetails and YOUTUBE_CATEGORIES consts here
const SerpDetails = ({ user, serpData, targetKeyword, isGeneratingTitles, titleIdeas, onGenerateTitles, actionCosts, InfoTooltip }) => {
  // A safe function to render simple markdown (bold) as HTML
  const renderFormattedText = (text) => {
    if (!text) return null;
    const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return <p className="serp-insights-text" dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  // New SerpInsights sub-component
  const SerpInsights = ({ insights, tags, score, angleToBeat, summaryStats, opportunityContext }) => {
    const formatAvgAge = (days) => {
      if (days > 365) return `${(days / 365).toFixed(1)} years`;
      if (days > 30) return `${(days / 30).toFixed(1)} months`;
      return `${Math.round(days)} days`;
    };

    return (
      <div className="serp-insights-summary">
        <div className="serp-insights-header">
          <h3>Strategic Insights<InfoTooltip content="An AI-powered summary of the top-ranking video titles, identifying the dominant content angles and potential opportunities." /></h3>
          <div className="opportunity-score">
            <span>Opportunity:</span>
            <span className={`opportunity-pill ${score?.toLowerCase()}`}>{score}</span>
            <InfoTooltip content="<strong>High:</strong> High demand, low competition.<br/><strong>Medium:</strong> Good demand or low competition.<br/><strong>Low:</strong> Low demand and high competition." />
          </div>
        </div>
        
        {opportunityContext && (
          <p className="opportunity-context" dangerouslySetInnerHTML={{ __html: opportunityContext.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
        )}

        {renderFormattedText(insights)}

        {angleToBeat && (
          <div className="angle-to-beat">
            <h4>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
              </svg>
              Angle to Beat
            </h4>
            <p>{angleToBeat}</p>
          </div>
        )}

        {summaryStats && (
          <div className="serp-glance-summary">
            <div className="card-stat">
              <span className="card-stat-label">Avg. Video Age<InfoTooltip content="The average age of the top-ranking videos. A higher age suggests an opportunity for fresh content." /></span>
              <span className="card-stat-value">{formatAvgAge(summaryStats.average_age_days)}</span>
            </div>
            <div className="card-stat">
              <span className="card-stat-label">Newcomers<InfoTooltip content="The number of top-ranking videos published in the last 6 months. A higher number means new videos can break in." /></span>
              <span className="card-stat-value">{summaryStats.newcomer_count} / 8</span>
            </div>
            <div className="card-stat">
              <span className="card-stat-label">Title Density<InfoTooltip content="How many of the top 8 videos use the exact keyword in their title. A lower number is a good sign." /></span>
              <span className="card-stat-value">{summaryStats.title_keyword_density} / 8</span>
            </div>
          </div>
        )}

        {tags && tags.length > 0 && (
          <div style={{marginTop: '20px'}}>
            <h4 className="text-sm font-semibold mb-2 text-gray-300 flex items-center">Common Tags <InfoTooltip content="These are the most frequently used tags from the top-ranking videos. Use them to improve your video's discoverability." /></h4>
            <div className="common-tags-container">
              {tags.map(tag => (
                <span key={tag} className="common-tag">{tag}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const navigate = useNavigate();
  const formatCompact = (num) => Intl.NumberFormat('en-US', { notation: 'compact' }).format(num);
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    const daysPassed = Math.floor(seconds / 86400);

    let text = "Today";
    if (daysPassed > 365) {
      text = `${Math.floor(daysPassed / 365)} years ago`;
    } else if (daysPassed > 30) {
      text = `${Math.floor(daysPassed / 30)} months ago`;
    } else if (daysPassed > 0) {
      text = `${daysPassed} days ago`;
    }

    return { text };
  };

  const copyToClipboard = (text, clickEvent, label) => {
    navigator.clipboard.writeText(text);
    event({ action: 'copy_text', category: 'Engagement', label: `Keyword Research: ${label}` });
    const button = clickEvent.currentTarget; // Always get the button
    if (button) {
      const originalContent = button.innerHTML; // Save the SVG
      // Use a checkmark SVG for feedback to avoid text overflow
      const checkIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>`;
      button.innerHTML = checkIcon;
      button.classList.add('copied');
      setTimeout(() => {
        try {
          button.innerHTML = originalContent; // Restore the SVG
          button.classList.remove('copied');
        } catch (e) { /* Ignore errors if component unmounts */ }
      }, 1500);
    }
  };

  const handleGenerateClick = () => {
    // This event is now correctly fired in App.js's handleGenerateTitles function
    const competitorTitles = serpData.serp_videos.map(v => v.title);
    onGenerateTitles(targetKeyword, competitorTitles, serpData.strategic_insights, serpData.angle_to_beat);
  };

  return (
    <>
      <SerpInsights 
        insights={serpData.strategic_insights}
        tags={serpData.common_tags}
        score={serpData.opportunity_score}
        angleToBeat={serpData.angle_to_beat}
        summaryStats={serpData.serp_summary_stats}
        opportunityContext={serpData.opportunity_context}
      />

      <div className="serp-container">
          {serpData.serp_videos.map(video => {
            const ageInfo = timeAgo(video.published_at);
            return (
              <div key={video.id} className="serp-card">
                <a href={`https://www.youtube.com/watch?v=${video.id}`} target="_blank" rel="noopener noreferrer">
                  <img src={video.thumbnail_url} alt="Video thumbnail" className="serp-thumbnail" />
                </a>
                <h4 className="serp-title">{video.title}</h4>
                <p className="serp-channel">{video.channel_title}</p>
                <div className="serp-stats">
                  <span>👁️ Views: {formatCompact(video.view_count)}</span>
                  <span>👍 {formatCompact(video.like_count)}</span>
                  <span>💬 {formatCompact(video.comment_count)}</span>
                </div>
                <div className="serp-footer">
                  <span className="serp-vph">
                    <span className="font-bold">Avg. VPH: {video.vph}</span>
                    <InfoTooltip content="<strong>Avg. VPH (Lifetime):</strong><br />This is the average Views Per Hour over the video's lifetime, calculated by dividing total views by the hours since publication." />
                  </span>
                  <span className="serp-age font-semibold">{ageInfo.text}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="title-generator-section">
          {user.plan === 'pro' ? (
            <div className="title-generator-wrapper">
              <button onClick={handleGenerateClick} disabled={isGeneratingTitles} className="generate-titles-button">
                {isGeneratingTitles ? (
                  <>
                    <div className="button-spinner-light"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.898 20.572L16.5 21.75l-.398-1.178a3.375 3.375 0 00-2.455-2.456L12.75 18l1.178-.398a3.375 3.375 0 002.455-2.456L16.5 14.25l.398 1.178a3.375 3.375 0 002.456 2.456L20.25 18l-1.178.398a3.375 3.375 0 00-2.456 2.456z" /></svg>
                    <span>Generate Title Ideas</span>
                    <div className="cost-badge">
                      <span>{actionCosts.generate_titles}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" role="img" aria-label="credits: stacked coins"><defs><linearGradient id="coinFace" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fef08a"/><stop offset="100%" stopColor="#facc15"/></linearGradient><linearGradient id="coinEdge" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#eab308"/><stop offset="100%" stopColor="#ca8a04"/></linearGradient></defs><ellipse cx="32" cy="48" rx="20" ry="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="38" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="38" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="28" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="28" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="18" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="18" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/></svg>
                    </div>
                  </>
                )}
              </button>
              <InfoTooltip content="Uses the strategic analysis and 'Angle to Beat' above to generate titles designed to outperform the competition." />
            </div>
          ) : (
            <button onClick={() => {
                event({ action: 'click_upgrade_intent', category: 'Monetization', label: 'Source: Keyword Research Titles' });
                navigate('/pricing');
              }} className="generate-titles-button is-locked">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
              <span>Generate Title Ideas (Pro)</span>
            </button>
          )}
          {titleIdeas.length > 0 && (
            <div className="title-ideas-container">
              <ul className="title-ideas-list">
                {titleIdeas.map((title, index) => (
                  <li key={index} className="title-idea-item">
                    <span>{title}</span>
                    <button className="copy-title-btn" onClick={(e) => copyToClipboard(title, e, 'Title Idea')} title="Copy">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 8.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v8.25A2.25 2.25 0 006 16.5h2.25m8.25-8.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-7.5A2.25 2.25 0 018.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 00-2.25 2.25v6" /></svg>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
    </>
  );
};

const YOUTUBE_CATEGORIES = [
    { id: "0", name: "All Categories" }, { id: "1", name: "Film & Animation" }, { id: "2", name: "Autos & Vehicles" }, { id: "10", name: "Music" }, { id: "15", name: "Pets & Animals" }, { id: "17", name: "Sports" }, { id: "20", name: "Gaming" }, { id: "22", name: "People & Blogs" }, { id: "24", name: "Entertainment" }, { id: "26", name: "Howto & Style" }, { id: "27", name: "Education" }, { id: "28", name: "Science & Technology" },
  ];

const KeywordResearchTool = ({
    user,
    keyword,
    setKeyword,
    selectedCategory,
    setSelectedCategory,
    results,
    isLoading,
    error,
    handleSearch,
    handleAnalyzeSerp,
    onGenerateTitles,
    actionCosts
}) => {
  const navigate = useNavigate();
  return (
    <div className="tool-container keyword-research-container">
      <header className="tool-header">
        <h1>YouTube Keyword Research</h1>
        <p>Find high-demand, low-competition keywords to rank higher and grow faster.</p>
      </header>
      {/* Search container */}
      <div className="search-container">
        <div className="pill-search-bar">
          <select className="pill-search-category" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            {YOUTUBE_CATEGORIES.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
          </select>
          <div className="pill-search-divider"></div>
          <input 
            className="pill-search-input" 
            type="text" 
            value={keyword} 
            onChange={(e) => setKeyword(e.target.value)} 
            placeholder="Enter a topic or keyword..." 
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()} 
          />
          <button className="pill-search-button" onClick={handleSearch} disabled={isLoading}>
            {isLoading ? (
              <div className="button-spinner-light"></div>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="search-icon">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <div className="cost-badge">
                  <span>{actionCosts.initial_search}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" role="img" aria-label="credits: stacked coins">
                    <defs>
                      <linearGradient id="coinFace" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fef08a"/>
                        <stop offset="100%" stopColor="#facc15"/>
                      </linearGradient>
                      <linearGradient id="coinEdge" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#eab308"/>
                        <stop offset="100%" stopColor="#ca8a04"/>
                      </linearGradient>
                    </defs>
                    <ellipse cx="32" cy="48" rx="20" ry="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/>
                    <rect x="12" y="38" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/>
                    <ellipse cx="32" cy="38" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/>
                    <rect x="12" y="28" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/>
                    <ellipse cx="32" cy="28" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/>
                    <rect x="12" y="18" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/>
                    <ellipse cx="32" cy="18" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/>
                  </svg>
                </div>
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        error.toLowerCase().includes("credits") ?
        <UpgradeError message={error} source="Keyword Research" /> :
        <p className="error-message">{error}</p> // Keep for non-credit errors
      )}
      
      {results.length > 0 && (
        <div className="results-card-list">
          {results.map((item, index) => (
            <div key={item.keyword} className={`result-card ${item.isSerpVisible ? 'is-open' : ''}`}>
              {/* Main content of the card */}
              <div className="card-main-content">
                <p className="card-keyword">{item.keyword}</p>                
                <div className="card-stat-group">
                  <div className="card-stat">
                    <span className="card-stat-label">
                      Est. Volume
                      <InfoTooltip content="An estimate of search demand. A higher number is better. Click the arrow to analyze and reveal the score." />
                    </span>
                    {item.analysis ? (
                      <span className="card-stat-value">{new Intl.NumberFormat('en-US', { notation: 'compact' }).format(item.analysis.volume_proxy_views)}</span>
                    ) : (
                      <span className="card-stat-value no-stats">-</span>
                    )}
                  </div>
                  <div className="card-stat">
                    <span className="card-stat-label">
                      Competition
                      <InfoTooltip content="How difficult it is to rank for this keyword. 'Low' is a great opportunity. Click the arrow to analyze and reveal the score." />
                    </span>
                    {item.analysis ? (
                      <span className={`competition-pill ${item.analysis.competition.toLowerCase()}`}>{item.analysis.competition}</span>
                    ) : (
                      <span className="card-stat-value no-stats">-</span>
                    )}
                  </div>
                </div>
                <div className="card-actions">
                  {user.plan === 'pro' ? (
                    <>
                      <div className="cost-badge">
                        <span>{actionCosts.analyze_serp}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" role="img" aria-label="credits: stacked coins"><defs><linearGradient id="coinFace" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fef08a"/><stop offset="100%" stopColor="#facc15"/></linearGradient><linearGradient id="coinEdge" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#eab308"/><stop offset="100%" stopColor="#ca8a04"/></linearGradient></defs><ellipse cx="32" cy="48" rx="20" ry="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="38" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="38" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="28" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="28" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="18" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="18" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/></svg>
                      </div>
                      <button className="serp-toggle-btn" onClick={() => handleAnalyzeSerp(index)} disabled={item.isLoading} title={item.isSerpVisible ? "Hide Details" : "See SERP Details"}>
                      {item.isLoading ? ( <div className="button-spinner"></div> ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className={item.isSerpVisible ? 'arrow-down' : ''}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      )}
                      </button>
                    </>
                  ) : (
                    <button className="serp-toggle-btn is-locked" onClick={() => {
                        event({ action: 'click_upgrade_intent', category: 'Monetization', label: 'Source: Keyword Research SERP' });
                        navigate('/pricing');
                      }} title="Upgrade to Pro to analyze keywords">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* SERP details that expand below */}
              {item.isSerpVisible && item.analysis && (
                <div className="serp-details-wrapper">
                  <SerpDetails
                    user={user}
                    serpData={item.analysis}
                    targetKeyword={item.keyword}
                    isGeneratingTitles={item.isGeneratingTitles}
                    titleIdeas={item.titleIdeas || []}
                    onGenerateTitles={onGenerateTitles}
                    actionCosts={actionCosts} // Pass actionCosts down
                    InfoTooltip={InfoTooltip} // Pass the component down
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KeywordResearchTool;